import { PositionError } from "./PositionError"

/**
 * Checks, if the Geolocation API is available
 *
 * @returns {boolean}
 */
function isGeolocationAvailable() {
    return navigator.geolocation.watchPosition instanceof Function
}

/**
 * The Geolocation.watchPositionUntil() method is used to register a
 * 	handler function that will be called automatically each time the
 * 	position of the device changes. You can also, optionally, specify
 * 	an error handling callback function.
 *
 * The difference to the Geolocation.watchPosition() method is the
 * 	timeout parameter, which triggers the error callback in case no
 * 	user interaction took place within the time span. It does not
 * 	cancel the watch, however, so the users decision can stil have
 * 	an effect afterward. It can be used to display default content
 * 	until the point the user decides to share their location, to fetch
 * 	the actual data.
 * 	In case the watch should be terminated the positionError object
 * 	passed to the error handler has an additional member 'watchId'
 * 	that can be used to manually call navigator.geolocation.clearWatch
 *
 * @param {int} timeout
 * 	A positive long value representing the maximum length of time
 * 	(in milliseconds) the script is allowed to run before the request
 * 	sent to the user times out
 * @param {Function} success
 * 	A callback function that takes a Position object as an
 * 	input parameter.
 * @param {Function} error
 *	An optional callback function that takes a PositionError object
 *	as an input parameter.
 * @param {Object} options
 * 	An optional PositionOptions object.
 */
export function getCurrentPositionUntil(
    this: any,
    timeout: number,
    success: (position: GeolocationPosition) => void,
    error: () => void,
    newOptions: object,
) {
    const successCallback = success.bind(this)
    let errorCallback = ((_: PositionError | GeolocationPositionError) => {}).bind(this)
    let watchAttempt = 0
    let options = newOptions

    if (undefined !== error) {
        errorCallback = error.bind(this)
    }

    if (undefined !== options) {
        options = {}
    }

    if (!isGeolocationAvailable()) {
        const positionError = new PositionError(
            PositionError.GEOLOCATION_UNAVAILABLE,
            "The Geolocation API is unavailable in this browser",
        )

        errorCallback(positionError)
    }

    const geoLocationTimeoutId = window.setTimeout(() => {
        const positionError = new PositionError(
            PositionError.REQUEST_TIMEOUT,
            "The location request times out without user input",
        )

        positionError.watchId = geoLocationWatchId

        errorCallback(positionError)
    }, timeout)

    const geoLocationWatchId = navigator.geolocation.watchPosition(
        (position) => {
            window.clearTimeout(geoLocationTimeoutId)

            // @ts-expect-error TODO fix, for now it justs adds to geoLocation
            position.attempt = ++watchAttempt
            // @ts-expect-error TODO fix, for now it justs adds to geoLocation
            position.watchId = geoLocationWatchId

            successCallback(position)
        },
        (positionError) => {
            window.clearTimeout(geoLocationTimeoutId)
            navigator.geolocation.clearWatch(geoLocationWatchId)

            errorCallback(positionError)
        },
        options,
    )
}
